import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { SpaceConfigurator } from '@mono/space-configurator';

import { IWidgetConfiguratorProps } from '../../types/IWidgetBlock';
import { DataTableConfigBlockState } from '../types';
import DataTable from '..';
import BasicSettingsConfigurator from './BasicSettingsConfigurator';
import DatabilitiesConfigurator from './DatabilitiesConfigurator';
import RiskLevelsConfigurator from './RiskLevelsConfigurator';
import ThresholdConfigurator from './ThresholdConfigurator';

type WidgetConfiguratorProps = IWidgetConfiguratorProps<DataTableConfigBlockState>;

const componentsToDisplayDefaults = {
    basicSettings: true,
    databilities: true,
    riskLevels: true,
    thresholds: true,
    spaceSettings: false,
};

const WidgetConfigurator: React.FC<WidgetConfiguratorProps> = ({ viewblockState, setViewblockState, baseDatability, componentsToDisplay }) => {
    const [baseSpace, setBaseSpace] = useState(
        viewblockState.databilities[0]?.space ??
            baseDatability.space ?? {
                type: 'Point' as const,
                coordinates: [173, -40] as [number, number],
            }
    );
    const newBaseDatability = {
        ...baseDatability,
        space: baseSpace,
        data_structure: 'timeseries/point2d',
    };

    const componentsToDisplayMerged = useMemo(
        () => ({
            ...componentsToDisplayDefaults,
            ...componentsToDisplay,
        }),
        [componentsToDisplay]
    );

    useEffect(() => {
        if (DataTable.migrateState) {
            setViewblockState(DataTable.migrateState(viewblockState));
        }
    }, []);

    return (
        <Tabs destroyInactiveTabPane style={{ maxWidth: '100%' }}>
            {componentsToDisplayMerged.basicSettings && (
                <Tabs.TabPane tab='General settings' key='general'>
                    <BasicSettingsConfigurator viewblockState={viewblockState} setViewblockState={setViewblockState} />
                </Tabs.TabPane>
            )}
            {componentsToDisplayMerged.spaceSettings && (
                <Tabs.TabPane tab='Space settings' key='space'>
                    <SpaceConfigurator
                        enableSite
                        enableSpace
                        initialState={{
                            space: baseSpace,
                            movSiteId: viewblockState.settings.movSiteId,
                        }}
                        onChange={(props) => {
                            if (!props.space) return;
                            setBaseSpace(props.space);
                            setViewblockState((prev) => {
                                return {
                                    ...prev,
                                    databilities: prev.databilities.map((d) => {
                                        return { ...d, space: props.space };
                                    }),
                                    settings: { ...prev.settings, movSiteId: props.movSiteId },
                                };
                            });
                        }}
                    />
                </Tabs.TabPane>
            )}
            {componentsToDisplayMerged.databilities && (
                <Tabs.TabPane tab='Data definition' key='data-definition'>
                    <DatabilitiesConfigurator baseDatability={newBaseDatability} viewblockState={viewblockState} setViewblockState={setViewblockState} />
                </Tabs.TabPane>
            )}
            {componentsToDisplayMerged.riskLevels && (
                <Tabs.TabPane tab='Risk levels' key='risk-levels'>
                    <RiskLevelsConfigurator viewblockState={viewblockState} setViewblockState={setViewblockState} />
                </Tabs.TabPane>
            )}
            {componentsToDisplayMerged.thresholds && (
                <Tabs.TabPane tab='Thresholds' key='thresholds'>
                    <ThresholdConfigurator viewblockState={viewblockState} setViewblockState={setViewblockState} />
                </Tabs.TabPane>
            )}
        </Tabs>
    );
};

export default WidgetConfigurator;
